import {
  AdjustedItemPrice,
  PagedFilter,
  GeoLocation,
  PaymentMethod,
  PaymentType
} from '../common/common.types';
import { Activity } from './activities/activity.types';
import { EventNote, EventNoteType } from './notes/event-note.types';
import { EventOccasion, EventType, EventVertical } from '../meta/meta.types';
import {
  EventCustomerSummary,
  EventCustomer,
  NewEventCustomer
} from './customers/event-customer.types';
import { Moment } from 'moment';
import { EnumValue } from '../common/enum.consts';

export class EventSummary {
  eventId: number;
  extendedEventId: string;
  legacyBookingId?: number;
  vertical: EventVertical;
  type?: EventType;
  occasion?: EventOccasion;

  name?: string;
  status: EventStatus;

  bookedOn?: string | Moment;
  cancelledOn?: string | Moment;
  startsOn?: string | Moment;
  endsOn?: string | Moment;
  isSingleDay?: boolean;
  timezone?: string;

  attendeeTotal?: number;
  currencyCode: string;
  totalPrice?: number;
  totalPaid?: number;
  amountDue: number;

  location?: GeoLocation;
  customers: EventCustomerSummary[];
  enquiryDetails?: EventEnquiryDetails;

  deposit?: number;
  depositDue?: Moment | string;
  paymentDue?: Moment | string;
}

export class EventFilter extends PagedFilter {
  statuses?: EventStatus[];
  eventVerticalIds?: number[];
  eventTypeIds?: number[];
  eventBefore?: string | Moment;
  eventAfter?: string | Moment;
  paymentStatus?: EventFilterPaymentStatus;
  conciergeIds?: number[];
  orderBy?: EventFilterOrder;
  onlyMine?: boolean;
  isPaymentOverdue?: boolean;
}

export enum EventFilterPaymentStatus {
  AwaitingDeposit = 1,
  PendingPayment = 2
}

export enum EventFilterOrder {
  EventDateAsc = 1,
  EventDateDesc = 2,
  CreatedOnAsc = 3,
  CreatedOnDesc = 4,
  BookedOnAsc = 5,
  BookedOnDesc = 6
}

export const EventFilterOrderValues: EnumValue<number>[] = [
  { value: EventFilterOrder.EventDateAsc, name: 'Event On: Earliest' },
  { value: EventFilterOrder.EventDateDesc, name: 'Event On: Latest' },
  { value: EventFilterOrder.CreatedOnAsc, name: 'Enquired On: Old to New' },
  { value: EventFilterOrder.CreatedOnDesc, name: 'Enquired On: New to Old' },
  { value: EventFilterOrder.BookedOnAsc, name: 'Booked On: Old to New' },
  { value: EventFilterOrder.BookedOnDesc, name: 'Booked On: New to Old' }
];

export class Event {
  eventId: number;
  extendedEventId: string;
  legacyBookingId?: number;
  vertical: EventVertical;
  type?: EventType;
  occasion?: EventOccasion;

  status: EventStatus;
  name?: string;
  startsOn?: Moment | string;
  endsOn?: Moment | string;
  timezone?: string;
  isSingleDay: boolean;

  attendeeTotal?: number;
  currencyCode: string;
  price: AdjustedItemPrice;

  totalPrice?: number;
  totalPaid?: number;
  totalDue?: number;
  deposit?: number;
  depositDue?: Moment | string;
  paymentDue?: Moment | string;

  customers: EventCustomer[];
  payments: EventPayment[];
  notes: EventNote[];
  activities: Activity[];

  location?: GeoLocation;
  enquiryDetails: EventEnquiryDetails;
  concierge: string;

  isOrganiser: boolean;
  createdOn: Moment | string;
}

export class EventToAction {
  eventId: number;

  noteId: number;
  actionOn: Moment | string;
  noteConcierge: string;
  note: string;
  actionStatus?: EventStatus;
  noteType?: EventNoteType;

  event: Event;
}

export class EventsToActionFilter extends PagedFilter {
  currencyCode?: string;
  conciergeIds?: number[];
  eventConciergeIds?: number[];
  statuses?: EventStatus[];
  noteTypes?: EventNoteType[];
  eventVerticalIds?: number[];
  eventTypeIds?: number[];
  orderbyEnquiryDate?: EventToActionFilterOrder;

  eventAfter?: string | Moment;  
  eventBefore?: string | Moment;
  actionAfter?: string | Moment;
  actionBefore?: string | Moment;
}

export enum EventToActionFilterOrder {
  CreatedOnAsc = 1,
  CreatedOnDesc = 2
}

export enum EventStatus {
  Enquiry = 0,
  Draft = 2,
  Quoted = 4,
  RequestToBook = 5,
  Tentative = 6,
  Booked = 8,
  Confirmed = 9,
  Complete = 10,
  RescheduleRequested = 12,
  Postponed = 13,
  CancelRequested = 14,

  Declined = 98,
  Cancelled = 99
}

export class EventPayment {
  eventPaymentId: number;
  paymentMethod: PaymentMethod;
  paymentType: PaymentType;
  amount: number;
  authorizedAmount?: number;
  currencyCode: string;
  notes: string;

  customerId: number;
  addedBy: string;
  capturedBy?: string;
  createdOn: Moment | string;
  giftCardId?: string;
}

export class NewEvent {
  status: EventStatus;
  eventTemplateId?: number;
  eventVerticalId?: number;
  eventTypeId?: number;
  eventOccasionId?: number;
  name: string;
  startsOn?: string | Moment;
  endsOn?: string | Moment;
  timezone?: string;
  attendeeTotal?: number;
  actionOn?: string;
  quoteComment?: string;
  comment?: string;
  price: AdjustedItemPrice;
  location?: GeoLocation;
  enquiryDetails?: EventEnquiryDetails;
  customers?: NewEventCustomer[];
}

export class UpdatedEvent {
  eventVerticalId?: number;
  eventTypeId?: number;
  eventOccasionId?: number;
  name: string;
  startsOn?: string;
  endsOn?: string;
  timezone?: string;
  attendeeTotal?: number;
  price: AdjustedItemPrice;
  enquiryDetails?: EventEnquiryDetails;
  location?: GeoLocation;

  deposit?: number;
  depositDue?: Moment | string;
  paymentDue?: Moment | string;
}

export class EventEnquiryDetails {
  questions?: {
    [key: string]: string;
  };
  servicePromise?: string;
  source?: string;
}

export class EventUpdateRequest {
  startsOn?: string;
  attendeeTotal?: number;
  address?: string;
  comments?: string;
}

export class EventStatusChange {
  actionOn?: string | Moment;
  rescheduleRequestDate?: string | Moment;
  rescheduleRequestStatus?: EventStatus;
  comment?: string;
  sendEmail?: boolean;
  emailNotes?: string;

  deposit?: number;
  depositDue?: Moment | string;
  paymentDue?: Moment | string;
}
